import { useAdFleetMapVehicles } from '@backoffice-frontend/ad-fleet-map'
import { useAdOperatorAvailability } from '@backoffice-frontend/ad-operator-availability'
import {
  getOnBoardPassengers,
  getPassengerCount,
} from '@backoffice-frontend/ad-passenger-list'
import { useAdVehicleStream } from '@backoffice-frontend/ad-vehicle-stream'
import {
  adCallAlertTypes,
  getBackgroundColor as getAlertBackgroundColor,
  getAlertIconComponent,
  getAlertSeverity,
  getBorderColor,
  getDisabledForegroundColor,
  getForegroundTextColor,
  useTaskTypeTranslation,
} from '@backoffice-frontend/alerts-common'
import { INFO_NOT_AVAILABLE_NA } from '@backoffice-frontend/common'
import { FccAdAlertStatus } from '@backoffice-frontend/graphql'
import { useCountUpSince } from '@backoffice-frontend/hooks'
import { Divider } from '@backoffice-frontend/patterns'
import { useUserHasAccess } from '@backoffice-frontend/restricted'
import { useMetricTracking } from '@backoffice-frontend/tracking'
import { useBackofficeLoggedInUser } from '@backoffice-frontend/user-management-ui'
import type { Theme } from '@emotion/react'
import { BoumRight } from '@moia-dev/moia-token-claims'
import { MoreVerticalIcon, PersonIcon } from '@moia-dev/pace-icons'
import {
  ActionItem,
  ActionMenu,
  Button,
  Dialog,
  Icon,
  IconButton,
  LoadingIndicator,
  Text,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  useActionMenu,
  useDialog,
} from '@moia-dev/pace-web'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import type {
  AdAlertFeedCardAlertFragment,
  AdAlertFeedCardVehicleFragment,
} from './AdAlertFeedCard.hook'

const MAX_VISIBLE_ALERTS = 2

export type CardState =
  | 'unassigned'
  | 'unassigned-hover'
  | 'assigned'
  | 'mine'
  | 'muted'

type AdAlertFeedCardProps = {
  vehicle: AdAlertFeedCardVehicleFragment
  isCurrentUserBusy?: boolean
}

export const AdAlertFeedCard = ({
  vehicle,
  isCurrentUserBusy = false,
}: AdAlertFeedCardProps) => {
  const { t } = useTranslation()
  const { user } = useBackofficeLoggedInUser()
  const { isAvailable, isOffline } = useAdOperatorAvailability()
  const { selectVehicle } = useAdFleetMapVehicles()
  const { isHandledByCurrentUser: isStreamOpen, vehicleId: streamVehicleId } =
    useAdVehicleStream()
  const { trackMetric } = useMetricTracking()
  const [isWaitingForStream, setIsWaitingForStream] = useState(false)
  const waitingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const clearWaitingTimeout = useCallback(() => {
    if (waitingTimeoutRef.current) {
      clearTimeout(waitingTimeoutRef.current)
      waitingTimeoutRef.current = null
    }
  }, [])

  // Clear waiting state when stream opens for this vehicle
  useEffect(() => {
    if (isWaitingForStream && isStreamOpen && streamVehicleId === vehicle.id) {
      setIsWaitingForStream(false)
      clearWaitingTimeout()
    }
  }, [
    isWaitingForStream,
    isStreamOpen,
    streamVehicleId,
    vehicle.id,
    clearWaitingTimeout,
  ])

  // Timeout: log metric and clear waiting state after 10s
  useEffect(() => {
    if (!isWaitingForStream) return

    waitingTimeoutRef.current = setTimeout(() => {
      console.error('[AdVehicleStream] STREAM_OPEN_TIMEOUT on alert card', {
        vehicleId: vehicle.id,
        backofficeUserId: user?.id,
      })
      trackMetric({
        metricName: 'AdVehicleStreamOpenTimeout',
        value: 1,
        dimensions: [
          { name: 'vehicleId', value: vehicle.id },
          { name: 'backofficeUserId', value: user?.id ?? 'unknown' },
        ],
      })
      setIsWaitingForStream(false)
    }, 5_000)

    return clearWaitingTimeout
  }, [
    isWaitingForStream,
    vehicle.id,
    trackMetric,
    clearWaitingTimeout,
    user?.id,
  ])

  const [isHovered, setIsHovered] = useState(false)
  const [isActionMenuOpen, setIsActionMenuOpen] = useState(false)
  const { getReferenceProps, ...actionMenuProps } = useActionMenu({
    isOpen: isActionMenuOpen,
    setIsOpen: setIsActionMenuOpen,
    placement: 'bottom-end',
  })
  const takeOverConfirmDialog = useDialog()
  const getTaskTypeTranslation = useTaskTypeTranslation()

  const canReadBackofficeUserData = useUserHasAccess({
    requiredRights: [BoumRight.BACKOFFICE_USER_USER_R],
  })

  const canUnassignOperator = useUserHasAccess({
    requiredRights: [BoumRight.AD_FLEET_CONTROL_EMERGENCY_STOP],
  })

  const { startHandling: startHandlingVehicle, isStartingHandling: loading } =
    useAdVehicleStream()

  const alerts =
    vehicle.fccCurrentAdAlerts?.alerts.filter(
      alert => alert.status !== FccAdAlertStatus.Closed,
    ) ?? []

  if (alerts.length === 0) {
    return null
  }

  // Backend sorts alerts by severity, so first alert is highest severity
  const highestSeverityAlert = alerts[0]
  const handledByOperator = vehicle.fccHandledByOperator
  const isAssignedToMe = handledByOperator?.id === user?.id
  const isAssignedToOther = handledByOperator && !isAssignedToMe
  const isAssigned = isAssignedToMe || isAssignedToOther

  const visibleAlerts = alerts.slice(0, MAX_VISIBLE_ALERTS)

  const cardState: CardState = (() => {
    if (isAssignedToOther) {
      return 'assigned'
    }
    if (isOffline) {
      return 'muted'
    }
    if (isAssignedToMe) {
      return 'mine'
    }
    if (isCurrentUserBusy) {
      return 'muted'
    }
    if (isHovered && isAvailable) {
      return 'unassigned-hover'
    }
    return 'unassigned'
  })()

  const passengerCount = getPassengerCount(
    getOnBoardPassengers(vehicle.fccUpcomingWaypoints ?? []),
  )

  const operatorName =
    canReadBackofficeUserData && handledByOperator
      ? `${handledByOperator.firstName} ${handledByOperator.lastName?.charAt(0) ?? ''}.`
      : INFO_NOT_AVAILABLE_NA

  const canStartHandling =
    isAvailable && !isAssignedToOther && !isAssignedToMe && !isCurrentUserBusy

  const canTakeOverFromOther =
    isAvailable && isAssignedToOther && !isCurrentUserBusy

  const hasCallAlert = alerts.some(
    alert =>
      adCallAlertTypes.includes(alert.type) &&
      alert.status === FccAdAlertStatus.Open,
  )

  const startHandling = async () => {
    console.info('[AdVehicleStream] startHandling called', {
      vehicleId: vehicle.id,
      canStartHandling,
      loading,
    })

    if (!canStartHandling || loading || isWaitingForStream) {
      console.info('[AdVehicleStream] startHandling blocked by guard', {
        vehicleId: vehicle.id,
        canStartHandling,
        loading,
        isWaitingForStream,
        isAvailable,
        isAssignedToOther,
        isAssignedToMe,
        isCurrentUserBusy,
      })
      return
    }

    setIsWaitingForStream(true)
    await startHandlingVehicle({
      vehicleId: vehicle.id,
      onCompleted: () => {
        console.info(
          '[AdVehicleStream] startHandling mutation completed, selecting vehicle',
          { vehicleId: vehicle.id },
        )
        selectVehicle(vehicle.id)
      },
    })
  }

  const executeTakeOverFromOther = async () => {
    console.info('[AdVehicleStream] executeTakeOverFromOther called', {
      vehicleId: vehicle.id,
      canTakeOverFromOther,
      loading,
    })

    if (!canTakeOverFromOther || loading || isWaitingForStream) {
      console.info(
        '[AdVehicleStream] executeTakeOverFromOther blocked by guard',
        {
          vehicleId: vehicle.id,
          canTakeOverFromOther,
          loading,
          isWaitingForStream,
        },
      )
      return
    }

    setIsWaitingForStream(true)
    await startHandlingVehicle({
      vehicleId: vehicle.id,
      isTakeOver: true,
      onCompleted: () => {
        console.info(
          '[AdVehicleStream] executeTakeOverFromOther mutation completed, selecting vehicle',
          { vehicleId: vehicle.id },
        )
        selectVehicle(vehicle.id)
      },
    })
  }

  const handleTakeOverFromOther = () => {
    if (!canTakeOverFromOther || loading) return

    if (hasCallAlert) {
      takeOverConfirmDialog.openDialog()
    } else {
      executeTakeOverFromOther()
    }
  }

  const handleVehicleClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    selectVehicle(vehicle.id)
  }

  return (
    <div
      onClick={
        canStartHandling && !isWaitingForStream ? startHandling : undefined
      }
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      css={theme => ({
        cursor: canStartHandling && !isWaitingForStream ? 'pointer' : 'default',
        border:
          cardState === 'assigned' || cardState === 'muted'
            ? `1px solid ${theme.semantic.ColorAccessoryReduced}`
            : `1px solid ${getBorderColor(highestSeverityAlert, theme)}`,
        borderTopWidth: cardState === 'assigned' ? '1px' : '8px',
        backgroundColor: getBackgroundColor(cardState, theme),
        padding: theme.space.Space2,
        opacity: loading ? 0.7 : 1,
        borderRadius: theme.radius.RadiusMedium,
      })}
    >
      <div
        css={theme => ({
          display: 'flex',
          alignItems: 'center',
          gap: theme.space.Space2,
          paddingBottom: theme.space.Space2,
        })}
      >
        <SeverityIcon
          alert={highestSeverityAlert}
          isMuted={cardState === 'assigned'}
        />
        <Text
          size="lg"
          onClick={!isCurrentUserBusy ? handleVehicleClick : undefined}
          css={theme => ({
            cursor: !isCurrentUserBusy ? 'pointer' : 'default',
            fontWeight: 500,
            color: getTextColor(
              cardState,
              theme,
              theme.semantic.ColorActionSecondaryDefault,
            ),
            '&:hover': {
              textDecoration: !isCurrentUserBusy ? 'underline' : 'none',
            },
          })}
        >
          {vehicle.label}
        </Text>
        <PassengerBadge count={passengerCount} cardState={cardState} />
        {isAssignedToOther && canUnassignOperator && (
          <div
            css={{
              marginLeft: 'auto',
              visibility: isHovered || isActionMenuOpen ? 'visible' : 'hidden',
            }}
          >
            <IconButton
              size="compact"
              variant="plain"
              icon={<MoreVerticalIcon />}
              ref={actionMenuProps.refs.setReference}
              onClick={e => e.stopPropagation()}
              {...getReferenceProps()}
            />
            <ActionMenu {...actionMenuProps}>
              <ActionItem
                disabled={!canTakeOverFromOther || loading}
                onClick={() => {
                  setIsActionMenuOpen(false)
                  handleTakeOverFromOther()
                }}
                label={t('common_takeOver_button')}
              />
            </ActionMenu>
          </div>
        )}
      </div>

      <Divider />

      <div
        css={theme => ({
          display: 'flex',
          flexDirection: 'column',
          paddingTop: theme.space.Space2,
        })}
      >
        {visibleAlerts.map((alert, index) => {
          const isLastRow = index === visibleAlerts.length - 1
          return (
            <AlertRow
              key={alert.id}
              alertType={getTaskTypeTranslation(alert.type)}
              createdAt={alert.createdAt}
              showTimestamp={!isAssigned && !isWaitingForStream}
              cardState={cardState}
              operatorName={isLastRow && isAssigned ? operatorName : undefined}
              trailing={
                isLastRow && isWaitingForStream ? (
                  <LoadingIndicator size={16} />
                ) : undefined
              }
            />
          )
        })}
      </div>

      <Dialog
        id={takeOverConfirmDialog.id}
        title={t('adOperatorDrawer_takeOverWithCallAlert_header')}
        onClose={takeOverConfirmDialog.closeDialog}
        actions={
          <>
            <Button
              onClick={takeOverConfirmDialog.closeDialog}
              label={t('common_cancel_button')}
              variant="secondary"
              size="compact"
            />
            <Button
              onClick={() => {
                takeOverConfirmDialog.closeDialog()
                executeTakeOverFromOther()
              }}
              label={t('common_takeOver_button')}
              size="compact"
              variant="critical"
            />
          </>
        }
      >
        <p>{t('adOperatorDrawer_takeOverWithCallAlert_caption')}</p>
      </Dialog>
    </div>
  )
}

type AlertRowProps = {
  alertType: string
  createdAt: string
  showTimestamp: boolean
  cardState: CardState
  operatorName?: string
  trailing?: React.ReactNode
}

const AlertRow = ({
  alertType,
  createdAt,
  showTimestamp,
  cardState,
  operatorName,
  trailing,
}: AlertRowProps) => {
  const timestamp = useCountUpSince({
    since: showTimestamp ? new Date(createdAt) : undefined,
  })
  return (
    <div
      css={theme => ({
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: theme.space.Space1,
      })}
    >
      <Tooltip delay={2000}>
        <TooltipTrigger>
          <Text
            size="md"
            weight="emphasized"
            css={theme => ({
              color: getTextColor(cardState, theme),
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              flex: 1,
              minWidth: 0,
            })}
          >
            {alertType}
          </Text>
        </TooltipTrigger>
        <TooltipContent>{alertType}</TooltipContent>
      </Tooltip>
      {trailing ??
        (operatorName ? (
          <Text
            size="sm"
            css={theme => ({
              color: getTextColor(
                cardState,
                theme,
                theme.semantic.ColorContentReduced,
              ),
            })}
          >
            {operatorName}
          </Text>
        ) : (
          timestamp && (
            <Text
              size="sm"
              weight="emphasized"
              css={theme => ({
                color: getTextColor(
                  cardState,
                  theme,
                  theme.semantic.ColorContentReduced,
                ),
                minWidth: '4ch',
              })}
            >
              {timestamp}
            </Text>
          )
        ))}
    </div>
  )
}

type SeverityIconProps = {
  alert: AdAlertFeedCardAlertFragment
  isMuted?: boolean
}

const SeverityIcon = ({ alert, isMuted = false }: SeverityIconProps) => {
  const severity = getAlertSeverity(alert)
  const IconComponent = getAlertIconComponent(severity)

  return (
    <Icon
      size={24}
      css={theme => ({
        backgroundColor: getAlertBackgroundColor(alert, theme),
        borderRadius: 4,
      })}
    >
      <IconComponent
        css={(theme: Theme) => ({
          color: isMuted
            ? getDisabledForegroundColor(alert, theme)
            : getForegroundTextColor(alert, theme),
        })}
      />
    </Icon>
  )
}

type PassengerBadgeProps = {
  count: number
  cardState: CardState
}

const PassengerBadge = ({ count, cardState }: PassengerBadgeProps) => {
  return (
    <div
      css={theme => ({
        display: 'flex',
        alignItems: 'center',
        gap: theme.space.Space1,
        backgroundColor: theme.semantic.ColorAccessoryReducedAlternative,
        borderRadius: 9999,
        padding: `${theme.space.Space05} ${theme.space.Space2}`,
      })}
    >
      <PersonIcon
        css={theme => ({
          width: 16,
          height: 16,
          color: getTextColor(cardState, theme),
        })}
      />
      <Text
        size="md"
        css={theme => ({
          color: getTextColor(cardState, theme),
        })}
      >
        {count}
      </Text>
    </div>
  )
}

const getBackgroundColor = (cardState: CardState, theme: Theme): string => {
  switch (cardState) {
    case 'unassigned-hover':
      return theme.semantic.ColorSurfaceOverlayHovered
    default:
      return theme.semantic.ColorSurfaceDefault
  }
}

const getTextColor = (
  cardState: CardState,
  theme: Theme,
  defaultColor?: string,
): string => {
  switch (cardState) {
    case 'assigned':
      return theme.semantic.ColorContentDisabled
    case 'muted':
      return theme.semantic.ColorContentReduced
    default:
      return defaultColor ?? theme.semantic.ColorContentDefault
  }
}
